import * as React from 'react';
import {
  FlatList,
  StyleSheet,
  Text,
  View,
  ActivityIndicator,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import BackSubheader from '../../elements/BackSubheader';
import ScreenContext from '../../ScreenContext';
import type {
  RecentOrderType,
  RecentOrderItemType,
  RecentOrderItemModifierType,
} from 'src/types/RecentOrders';
import ActionsFactory from 'src/actions/ActionsFactory';
import type {LocationType} from 'src/types/Location';
import AccountStore from 'src/stores/AccountStore';
import Styles from '../../Styles';
import RecentOrder from '../../elements/orderAhead/RecentOrder';
import CheckoutBar from '../../elements/orderAhead/CheckoutBar';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import AccountConstants from 'src/constants/AccountConstants';
import MenuService from 'src/services/MenuService';
import CartService from 'src/services/CartService';
import TransactionStore from 'src/stores/TransactionStore';
import CartTypes from 'src/constants/cart/CartTypes';
import Localized from 'src/constants/AppStrings';
import {confirm} from '../../helpers/AlertHelper';

type RecentOrdersScreenProps = {
  location: LocationType;
};

type RecentOrdersScreenState = {
  recentOrders: Array<RecentOrderType>;
  loaded: boolean;
};

class RecentOrdersScreen extends React.Component<
  RecentOrdersScreenProps,
  RecentOrdersScreenState
> {
  static contextType = ScreenContext;

  constructor(props: RecentOrdersScreenProps) {
    super(props);
    this.state = {
      recentOrders: [],
      loaded: false,
    };
    this.getCartItem = this.getCartItem.bind(this);
    this.addItem = this.addItem.bind(this);
    this.addAllItems = this.addAllItems.bind(this);
    this.goToCart = this.goToCart.bind(this);
  }

  async componentDidMount() {
    const recentOrders = await ActionsFactory.getAccountActions().getRecentOrders(
      AccountStore.getAccountId(),
      this.props.location.id,
    );
    this.setState({
      loaded: true,
      recentOrders,
    });
  }

  addAllItems(order: RecentOrderType) {
    if (!this.areModifiersAllAvailableForOrder(order)) {
      confirm(
        Localized.Labels.modifiers_changed_prompt,
        () => {
          this.forceAddAllItems(order);
        },
        () => {},
        Localized.Labels.modifiers_changed,
      );
    } else {
      this.forceAddAllItems(order);
    }
  }

  forceAddAllItems(order: RecentOrderType) {
    const transactionItems = TransactionStore.getAllItems();
    const prepDefaults = TransactionStore.getPreparationMethodDefaults(
      this.props.location,
    );

    if (transactionItems && order && order.items) {
      const items = [...transactionItems];
      order.items.forEach((item) => {
        const cartItem = this.getCartItem(item);
        items.unshift(cartItem);
      });
      CartService.updateCart(
        this.props.location.id,
        AccountStore.getAccountId(),
        [],
        items,
        AccountConstants.SOS_LOCATION_TYPE,
        order.items[0].scancode,
        TransactionStore.getPickupTime(),
        prepDefaults.selectedMethod,
        this.context,
      );
    }
  }

  addItem(item: RecentOrderItemType) {
    if (!this.areModifiersAllAvailableForItem(item)) {
      confirm(
        Localized.Labels.modifiers_changed_prompt,
        () => {
          this.forceAddItem(item);
        },
        () => {},
        Localized.Labels.modifiers_changed,
      );
    } else {
      this.forceAddItem(item);
    }
  }

  forceAddItem(item: RecentOrderItemType) {
    const transactionItems = TransactionStore.getAllItems();
    const prepDefaults = TransactionStore.getPreparationMethodDefaults(
      this.props.location,
    );

    if (transactionItems) {
      const items = [...transactionItems];
      const cartItem = this.getCartItem(item);
      items.unshift({...cartItem});
      CartService.updateCart(
        this.props.location.id,
        AccountStore.getAccountId(),
        [],
        items,
        AccountConstants.SOS_LOCATION_TYPE,
        item.scancode,
        TransactionStore.getPickupTime(),
        prepDefaults.selectedMethod,
        this.context,
      );
    }
  }

  areModifiersAllAvailableForOrder(order: RecentOrderType) {
    if (order.items && order.items.length > 0) {
      for (let i = 0; i < order.items.length; i++) {
        const item = order.items[i];

        if (!this.areModifiersAllAvailableForItem(item)) {
          return false;
        }
      }
    }

    return true;
  }

  areModifiersAllAvailableForItem(item: RecentOrderItemType) {
    if (item.modifiers && item.modifiers.length > 0) {
      for (let i = 0; i < item.modifiers.length; i++) {
        const modifier = item.modifiers[i];

        if (!modifier.available) {
          return false;
        }
      }
    }

    return true;
  }

  getCartItem(item: RecentOrderItemType) {
    const cartItem = {
      BarCode: item.scancode,
      Modifiers: [],
      PrintGroups: item.printgroups,
    };

    if (item.modifiers && item.modifiers.length > 0) {
      item.modifiers.forEach((itemModifier: RecentOrderItemModifierType) => {
        if (itemModifier.available) {
          cartItem.Modifiers.push(
            MenuService.getCartModifier(itemModifier.keyReference),
          );
        }
      });
    }

    return cartItem;
  }

  goToCart() {
    NavActions.push(AppRoutes.Scan, {
      marketName: this.props.location.name,
      locationId: this.props.location.id,
      locationType: AccountConstants.SOS_LOCATION_TYPE,
      beaconId: this.props.location.broadcastid ?? this.props.location.id,
      cartType: CartTypes.OrderAhead,
      location: this.props.location,
      showPreparationMethod: this.props.location.hasdiningpreference,
      defaultPreparationMethod: this.props.location.diningpreference,
    });
  }

  render() {
    return (
      <BackSubheader title={Localized.Buttons.recent_orders}>
        <FlatList
          data={this.state.recentOrders}
          style={styles.ordersContainer}
          keyExtractor={(item) => item.saleHeaderId}
          renderItem={({item}) => (
            <RecentOrder
              recentOrder={item}
              strings={Localized}
              addItem={this.addItem}
              addAllItems={this.addAllItems}
            />
          )}
          ListEmptyComponent={
            this.state.loaded ? (
              <Text style={styles.emptyText}>
                {Localized.Labels.no_recent_orders}
              </Text>
            ) : (
              <View style={styles.activityContainer}>
                <ActivityIndicator color={Styles.primaryColor} size="large" />
              </View>
            )
          }
        />
        <CheckoutBar
          location={this.props.location}
          strings={Localized}
          onPress={this.goToCart}
        />
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  ordersContainer: {
    flex: 1,
  },
  emptyText: {
    textAlign: 'center',
    color: Styles.black,
    marginTop: Styles.Spacing.m3,
    fontSize: Styles.Fonts.f1,
  },
  activityContainer: {
    marginTop: Styles.Spacing.m3,
  },
});
export default withForwardedNavigationParams()(RecentOrdersScreen);
